import { MaterialIcon } from '../../ui/MaterialIcon';
import { gradientVariantForGlyph } from '../../../theme/iconGradient';

/**
 * 26, up from 22 (2026-09). The rail is a top-level destination list and its
 * glyphs read small next to the drawer's 18px row icons, even though they are
 * the larger of the two: a row icon sits beside 14px text, a rail icon sits
 * above an 11px caption with 26px of clear space either side, so the same
 * pixels carry less weight. Sized by eye against the rail, not derived from
 * another surface's icon.
 *
 * The ceiling was the chip `NavRailItem` drew behind the glyph, 44x32, where 32
 * would fill its height exactly and stop it reading as a container. That chip
 * is now a 52px square (`NAV_RAIL_TARGET_SIZE`, to match Create), so the ceiling is
 * looser, and the size is still the one chosen by eye above.
 */
export const RAIL_ICON_SIZE = '26px';
const RAIL_ICON_WEIGHT = 400;

interface NavRailIconProps {
  readonly name: string;
  /**
   * `gradient` (default) for rail ITEMS — the identity treatment. What makes
   * it safe is the 11px caption `NavRailItem` renders directly beneath every
   * one of them; the gradient does not clear 3:1 in light mode, so the glyph
   * must never be the only thing identifying the control.
   *
   * `solid` for a glyph on a filled brand button — Create — where the icon is
   * already white-on-accent and a brand gradient over a brand fill would be
   * near-invisible, throwing away the only contrast the control has.
   *
   * Help is icon-only too and still takes the gradient: visual consistency with
   * the rail was chosen over 1.4.11 there, deliberately. See HelpMenu.tsx for
   * the decision and what would undo the tradeoff.
   */
  readonly tone?: 'gradient' | 'solid';
}

/**
 * One optical size and stroke for every primary-rail glyph, including Create.
 *
 * Rail items are IDENTITY icons, so they carry the brand gradient (see
 * `theme/iconGradient.ts` for which icons do and why). The 11px label directly
 * beneath each one is what makes that safe: the gradient does not clear 3:1 in
 * light mode, so the glyph must never be the only thing naming the target.
 *
 * `NavRailItem` always renders that label; Create and Help are bare
 * `IconButton`s and do not. Create opts out via `tone="solid"` for the
 * brand-over-brand reason below; Help keeps the gradient by design decision.
 * If you add a rail control, the question to ask is not "is it in the rail?"
 * but "does a sighted user see a word next to it?".
 */
export function NavRailIcon({ name, tone = 'gradient' }: NavRailIconProps) {
  return (
    <MaterialIcon
      name={name}
      boxSize={RAIL_ICON_SIZE}
      fontSize={RAIL_ICON_SIZE}
      weight={RAIL_ICON_WEIGHT}
      // `inherit` is what lets the SOLID tone pick up the button's white. The
      // gradient tone needs no help here: `gradient` suppresses MaterialIcon's
      // own `color` itself, so the two can no longer be set apart.
      color="inherit"
      // `tone` is a rail-level word for a rail-level decision, and it stays —
      // but there is only ONE mechanism underneath it. `solid` is the absence
      // of the gradient, not a second treatment, so the two tones cannot drift.
      gradient={tone === 'solid' ? undefined : gradientVariantForGlyph(name)}
    />
  );
}
